import {pxToRem, responsiveFontSizes} from '../utils/getFontValue';

/**
 * Typography System - Enhanced for 4K Resolution Support
 * 
 * BACKWARD COMPATIBLE: All existing typography works as before
 * ENHANCED: Now includes proper scaling for 4K displays (3840x2160)
 * 
 * Font sizes automatically scale based on resolution:
 * - Laptop (1536px): Base size
 * - FHD (1920px): 1.2x scale
 * - QHD (2560px): 1.5x scale
 * - 4K (3200px): 2.0x scale
 * - 4K+ (3840px): 2.5x scale (Assembly line - visible from 7 meters)
 */

// LUXURY FONT FAMILIES - Premium automotive inspired
// const FONT_PRIMARY = "'Inter', 'Helvetica Neue', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"; // Modern, clean
// const FONT_SECONDARY = "'Playfair Display', 'Georgia', serif"; // Elegant, sophisticated
// const FONT_DISPLAY = "'Montserrat', 'Inter', sans-serif"; // Bold, contemporary
// const FONT_MONO = "'JetBrains Mono', 'SF Mono', 'Monaco', 'Inconsolata', monospace"; // Technical precision

const FONT_PRIMARY = "'Uni Neue', 'Helvetica Neue', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_SECONDARY = "'Uni Neue', 'Helvetica Neue', sans-serif";
const FONT_DISPLAY = "'Uni Neue', sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', 'Monaco', 'Inconsolata', monospace";

const typography = {
  fontFamily: FONT_PRIMARY,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontWeightExtraBold: 800,
  
  // LUXURY DISPLAY HEADINGS (Enhanced with 4K support)
  h1: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 700,
    lineHeight: 1.2,
    fontSize: pxToRem(48),
    letterSpacing: '-0.02em',
    ...responsiveFontSizes({sm: 32, md: 40, lg: 48, xl: 60, xxl: 72, xxxl: 96}),
  },
  h2: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 700,
    lineHeight: 1.25,
    fontSize: pxToRem(40),
    letterSpacing: '-0.01em',
    ...responsiveFontSizes({sm: 28, md: 32, lg: 40, xl: 50, xxl: 60, xxxl: 80}),
  },
  h3: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    lineHeight: 1.3,
    fontSize: pxToRem(32),
    letterSpacing: '-0.01em',
    ...responsiveFontSizes({sm: 24, md: 28, lg: 32, xl: 40, xxl: 48, xxxl: 64}),
  },
  h4: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    lineHeight: 1.4,
    fontSize: pxToRem(24),
    letterSpacing: '0em',
    ...responsiveFontSizes({sm: 20, md: 22, lg: 24, xl: 30, xxl: 36, xxxl: 48}),
  },
  h5: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    lineHeight: 1.4,
    fontSize: pxToRem(20),
    letterSpacing: '0em',
    ...responsiveFontSizes({sm: 18, md: 19, lg: 20, xl: 25, xxl: 30, xxxl: 40}),
  },
  h6: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(18),
    letterSpacing: '0.01em',
    ...responsiveFontSizes({sm: 16, md: 17, lg: 18, xl: 22, xxl: 27, xxxl: 36}),
  },
  
  // ELEGANT SUBTITLES (Enhanced with 4K support)
  subtitle1: {
    fontFamily: FONT_SECONDARY,
    fontWeight: 400,
    lineHeight: 1.6,
    fontSize: pxToRem(16),
    letterSpacing: '0.01em',
    ...responsiveFontSizes({sm: 14, md: 15, lg: 16, xl: 20, xxl: 24, xxxl: 32}),
  },
  subtitle2: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 500,
    lineHeight: 1.5,
    fontSize: pxToRem(14),
    letterSpacing: '0.02em',
    ...responsiveFontSizes({sm: 13, md: 14, lg: 14, xl: 17, xxl: 21, xxxl: 28}),
  },
  
  // BODY TEXT (Enhanced with 4K support - Critical for assembly line readability)
  body1: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 400,
    lineHeight: 1.6,
    fontSize: pxToRem(16),
    letterSpacing: '0.01em',
    ...responsiveFontSizes({sm: 14, md: 15, lg: 16, xl: 20, xxl: 24, xxxl: 32}),
  },
  body2: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: pxToRem(14),
    letterSpacing: '0.01em',
    ...responsiveFontSizes({sm: 13, md: 14, lg: 14, xl: 17, xxl: 21, xxxl: 28}),
  },
  body3: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    letterSpacing: '0.02em',
    ...responsiveFontSizes({sm: 11, md: 12, lg: 12, xl: 15, xxl: 18, xxxl: 24}),
  },
  
  // SPECIAL TEXT STYLES (Enhanced with 4K support)
  caption: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 400,
    lineHeight: 1.4,
    fontSize: pxToRem(12),
    letterSpacing: '0.03em',
    ...responsiveFontSizes({sm: 10, md: 11, lg: 12, xl: 15, xxl: 18, xxxl: 24}),
  },
  overline: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    lineHeight: 1.2,
    fontSize: pxToRem(11),
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    ...responsiveFontSizes({sm: 10, md: 11, lg: 11, xl: 14, xxl: 16, xxxl: 22}),
  },
  
  // INTERACTIVE ELEMENTS (Enhanced with 4K support - Important for touch targets)
  button: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    lineHeight: 1.4,
    fontSize: pxToRem(14),
    letterSpacing: '0.02em',
    textTransform: 'none', // More modern approach
    ...responsiveFontSizes({sm: 13, md: 14, lg: 14, xl: 18, xxl: 22, xxxl: 28}),
  },
  
  // TECHNICAL/MONOSPACE
  code: {
    fontFamily: FONT_MONO,
    fontWeight: 400,
    lineHeight: 1.4,
    fontSize: pxToRem(13),
    letterSpacing: '0em',
  },
  
  // LUXURY DISPLAY STYLES (Enhanced with 4K support)
  display: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 800,
    lineHeight: 1.1,
    fontSize: pxToRem(64),
    letterSpacing: '-0.03em',
    ...responsiveFontSizes({sm: 40, md: 52, lg: 64, xl: 80, xxl: 96, xxxl: 128}),
  },
  lead: {
    fontFamily: FONT_SECONDARY,
    fontWeight: 400,
    lineHeight: 1.7,
    fontSize: pxToRem(20),
    letterSpacing: '0em',
    ...responsiveFontSizes({sm: 16, md: 18, lg: 20, xl: 25, xxl: 30, xxxl: 40}),
  },
};

export default typography;
